import { useCallback, useEffect, useRef, useState } from "react";
import getClient from "../../../helpers/get-client";
import { logActivity } from "../../../helpers/activity-log";
import { esCorteDeJornada } from "../../../helpers/suelta-api";

const TARGET_SAMPLE_RATE = 16000;

/**
 * Hook de transcripción en streaming con Deepgram Nova-3.
 * Conecta WebSocket directo del browser a wss://api.deepgram.com/v1/listen
 * usando una API key efímera obtenida del backend (/api/medical_analysis/asr/realtime-token/).
 *
 * Eventos:
 *   - onPartial(text)  → texto provisorio (interim_results); se actualiza cada
 *     ~100-300ms mientras hablás.
 *   - onFinal(text)    → texto confirmado (Deepgram lo emite cuando detecta fin
 *     de frase / silencio); ya no cambia.
 *   - onError(err)
 *
 * Métodos:
 *   - start() → pide token, abre WS, captura mic, empieza a stream PCM.
 *   - stop()  → cierra todo (manda CloseStream para asegurar últimos finals).
 */
export function useDeepgramASR({
  onPartial,
  onFinal,
  // Se dispara cuando el médico DEJA DE HABLAR (Deepgram lo marca con
  // `speech_final`), no en cada frase confirmada. Es el corte natural de un
  // tramo: permite dictar, callarse, mover el cursor a otra casilla, y seguir
  // hablando sin tocar ningún botón.
  onFinDeTramo,
  onError,
  onUnexpectedClose,
  // Se dispara cuando la sesión terminó SIN una sola palabra habiendo grabado
  // de verdad. Es LA falla silenciosa del dictado: el popup dijo "Escuchando",
  // el médico habló, y no salió nada. Sin este aviso se entera cuando ya cerró
  // la ficha y el trabajo no está.
  onSinTexto,
  // Vocabulario clínico para sesgar el reconocimiento (fármacos, microorganismos,
  // exámenes). Lo arma el backend desde las Keywords de la DB. Es la ÚNICA
  // palanca de vocabulario médico que da nova-3 en español: no existe un modelo
  // médico en español, verificado contra la API el 01-sep-2026.
  keyterms = [],
  // De dónde se dicta: "turno" (la ficha, en el computador) o "mic_remoto" (el
  // celular haciendo de micrófono). Solo viaja a la telemetría, para poder
  // separar las fallas de cada superficie en vez de mirarlas todas juntas.
  origen = "turno",
} = {}) {
  const [recording, setRecording] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | connecting | recording | error
  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const closingRef = useRef(false);
  const gainRef = useRef(null);
  const keepAliveRef = useRef(null);
  // Timer de cierre forzado de stop(). SIN esto (bug hasta el 01-sep-2026) el
  // `setTimeout(cleanup, 2000)` quedaba vivo aunque el WS ya hubiera cerrado
  // solo, y si el médico volvía a apretar el mic dentro de esos 2s el cleanup
  // atrasado le mataba la sesión NUEVA: cerraba el AudioContext, paraba el
  // micrófono y cerraba el WebSocket recién abierto. Era exactamente el flujo
  // "dicté acá, ahora dicto allá" y el Pausar/Reanudar del popup.
  const closeTimerRef = useRef(null);
  // Guard de reentrada por REF y no por state: `status` llega un render tarde,
  // así que dos clicks seguidos abrían dos sesiones. Y al revés, un cleanup que
  // no reseteaba `status` dejaba el hook trabado en "connecting" para siempre.
  const startingRef = useRef(false);

  // ── Telemetría PHI-safe del dictado ──────────────────────────────────
  // Por qué existe: hasta el 06-sep-2026 el dictado era el único módulo que no
  // emitía un solo evento. Cada vez que se rompió —y se rompió muchas veces—
  // nos enteramos porque Sebastián lo contó días después. Un dictado que falla
  // en silencio es peor que uno que falla fuerte: el médico cree que grabó.
  //
  // NUNCA sale una letra de lo dictado: solo el LARGO (`n_chars`), la duración
  // y un enum de por qué terminó. El backend además filtra con su allowlist.
  //
  // Los callbacks van por REF a propósito. `cleanup` cierra la sesión de
  // telemetría, y si dependiera de una prop —que en Turno.js es una arrow
  // inline, nueva en cada render— `cleanup` cambiaría de identidad en cada
  // render y el efecto de desmontaje (`useEffect(() => () => cleanup())`)
  // dispararía un cleanup real en medio del dictado. Es decir: instrumentar
  // mal esto apaga el micrófono.
  const onSinTextoRef = useRef(onSinTexto);
  onSinTextoRef.current = onSinTexto;
  const origenRef = useRef(origen);
  origenRef.current = origen;
  const sesionRef = useRef(null);

  const abrirSesionTelemetria = useCallback(() => {
    let aparato = "escritorio";
    try {
      if (/iphone|ipad|ipod|android/i.test(navigator.userAgent || "")) aparato = "movil";
    } catch (_) {}
    sesionRef.current = {
      t0: Date.now(),
      tOpen: null,
      chars: 0,
      tramos: 0,
      motivo: "",
      aparato,
      origen: origenRef.current === "mic_remoto" ? "mic_remoto" : "turno",
    };
    logActivity("dictado_intento", {
      module: "dictado",
      meta: { source: sesionRef.current.origen, kind: aparato },
    });
  }, []);

  /** Marca por qué se está cayendo la sesión. La primera causa gana: la que
   *  viene después suele ser consecuencia (un `ws close` detrás de un error). */
  const marcarMotivo = useCallback((motivo) => {
    if (sesionRef.current && !sesionRef.current.motivo) sesionRef.current.motivo = motivo;
  }, []);

  const cerrarSesionTelemetria = useCallback(() => {
    const s = sesionRef.current;
    if (!s) return;
    sesionRef.current = null;
    const dur = Date.now() - s.t0;
    // Grabó de verdad y no salió una palabra: ESA es la falla silenciosa. Se
    // mide desde que el micrófono quedó abierto (no desde el click), y con un
    // piso de 5 s para no acusar al que abrió el mic y se arrepintió.
    const escuchando = s.tOpen ? Date.now() - s.tOpen : 0;
    const mudo = !s.motivo && s.tOpen && s.chars === 0 && escuchando >= 5000;
    const motivo = s.motivo || (mudo ? "mudo" : "ok");
    logActivity("dictado_fin", {
      module: "dictado",
      meta: {
        source: s.origen,
        kind: s.aparato,
        duration_ms: dur,
        n_chars: s.chars,
        count: s.tramos,
        ok: motivo === "ok",
        reason: motivo,
      },
    });
    if (mudo) {
      try { onSinTextoRef.current?.({ duration_ms: escuchando }); } catch (_) {}
    }
  }, []);

  // ── Reporte de uso real de dictado ────────────────────────────────────
  // El streaming va directo browser→Deepgram, así que el backend no ve cuánto
  // audio pasó. Antes cargaba el TTL completo de la key (10 min) por cada
  // apertura de micrófono, y con el tope diario en 10 min eso daba UNA sola
  // apertura al día. Ahora reportamos los segundos reales: heartbeat cada 60s
  // mientras se dicta (para no perder el consumo si se cierra la pestaña) y un
  // último reporte del resto al detener.
  const startedAtRef = useRef(null);
  const reportedRef = useRef(0);
  const heartbeatRef = useRef(null);

  const reportUsage = useCallback(() => {
    if (!startedAtRef.current) return;
    const elapsed = (Date.now() - startedAtRef.current) / 1000;
    const pending = elapsed - reportedRef.current;
    if (pending < 1) return;
    // Fire-and-forget: el contador nunca debe entorpecer el dictado. Pero el
    // avance de `reportedRef` va en el `then`, no antes: si el POST falla, ese
    // tramo tiene que volver a intentarse en el heartbeat siguiente en vez de
    // darse por cobrado y perderse.
    getClient()
      .post("/api/medical_analysis/asr/usage/", { seconds: pending })
      .then(() => { reportedRef.current = elapsed; })
      .catch(() => {});
  }, []);

  const cleanup = useCallback(() => {
    setRecording(false);
    // Se cierra ACÁ y no en cada rama de error porque cleanup es el único punto
    // por el que pasan TODAS las salidas: stop(), caída del WS, excepción en
    // start() y desmontaje del componente.
    cerrarSesionTelemetria();
    // Sin esto el hook quedaba trabado: si el usuario cancelaba mientras estaba
    // en "connecting", el guard de start() rechazaba en silencio TODO intento
    // posterior y el mic no volvía hasta recargar la página.
    setStatus("idle");
    startingRef.current = false;
    closingRef.current = false;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    reportUsage();
    startedAtRef.current = null;
    reportedRef.current = 0;
    if (gainRef.current) {
      try { gainRef.current.disconnect(); } catch (_) {}
      gainRef.current = null;
    }
    if (processorRef.current) {
      try { processorRef.current.disconnect(); } catch (_) {}
      processorRef.current = null;
    }
    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch (_) {}
      sourceRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) {}
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) {}
      streamRef.current = null;
    }
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (_) {}
      wsRef.current = null;
    }
  }, [reportUsage, cerrarSesionTelemetria]);

  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async () => {
    if (recording || startingRef.current) return;
    // Matar el cierre diferido de un stop() reciente ANTES de montar nada: si
    // no, el timer de la sesión anterior desarma la que estamos abriendo.
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    startingRef.current = true;
    setStatus("connecting");
    abrirSesionTelemetria();
    let resolvedModel = "nova-3";
    let resolvedLanguage = "es";
    const log = (msg, ...rest) => console.log("[Deepgram]", msg, ...rest);
    log("start() invocado");
    try {
      // 1. PRIMERO getUserMedia + AudioContext, en el user-gesture síncrono
      // del click. iOS Safari rechaza getUserMedia/AudioContext si no se
      // invocan en el mismo "tick" del click — cualquier `await` previo
      // (ej. fetch al backend) hace que falle silencioso. Esta es la causa
      // típica de "apreté el mic en mobile y no pasó nada".
      // El AudioContext se crea y se resume ANTES del await, todavía dentro del
      // gesto del usuario. Crearlo después de `await getUserMedia` (como estaba
      // hasta el 01-sep-2026) hace que iOS lo deje `suspended`: el WebSocket
      // abre, el popup dice "Escuchando", `onaudioprocess` no dispara nunca y
      // no aparece una sola palabra. Ese es el síntoma clásico de "en el iPhone
      // el micrófono no hace nada".
      const AudioCtor = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtor({ sampleRate: TARGET_SAMPLE_RATE });
      audioCtxRef.current = audioCtx;
      if (audioCtx.state === "suspended") {
        try { audioCtx.resume(); } catch (_) {}
      }

      log("pidiendo getUserMedia…");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      log("getUserMedia OK, tracks:", stream.getTracks().length);
      streamRef.current = stream;

      // iOS Safari ignora `sampleRate` — más abajo leemos el real del context
      // y se lo pasamos a Deepgram para que resamplee del lado del server.
      const source = audioCtx.createMediaStreamSource(stream);
      sourceRef.current = source;
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      // Los primeros ~2s de voz se perdían: el micrófono ya estaba abierto pero
      // el processor recién se conectaba en `ws.onopen`, y el popup mientras
      // tanto decía "Empezá a hablar…". Ahora capturamos desde YA y lo que se
      // dicte antes de que el WebSocket esté listo queda en cola; se manda todo
      // junto al abrir. Tope de 10s para no acumular sin techo.
      const pendientes = [];
      const MAX_PENDIENTE = Math.ceil((10 * TARGET_SAMPLE_RATE) / 4096);
      const aInt16 = (float32) => {
        const int16 = new Int16Array(float32.length);
        for (let i = 0; i < float32.length; i += 1) {
          const v = Math.max(-1, Math.min(1, float32[i]));
          int16[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
        }
        return int16;
      };
      processor.onaudioprocess = (event) => {
        const int16 = aInt16(event.inputBuffer.getChannelData(0));
        const sock = wsRef.current;
        if (sock && sock.readyState === WebSocket.OPEN) {
          try { sock.send(int16.buffer); } catch (_) {}
          return;
        }
        if (pendientes.length < MAX_PENDIENTE) pendientes.push(int16.buffer);
      };
      const mute = audioCtx.createGain();
      mute.gain.value = 0;
      gainRef.current = mute;
      source.connect(processor);
      // El ScriptProcessor necesita un destino para que corra su callback, pero
      // NO queremos devolver el micrófono a los parlantes: sale por un GainNode
      // en 0. Colgarlo de `destination` directo forzaba en iOS la categoría de
      // audio play-and-record (baja el volumen del parlante y cambia el ruteo).
      processor.connect(mute);
      mute.connect(audioCtx.destination);

      log("AudioContext sampleRate real:", audioCtx.sampleRate);
      // 2. Pedir temp key (o master fallback) al backend.
      log("pidiendo token al backend…");
      const resp = await getClient().post("/api/medical_analysis/asr/realtime-token/");
      const resolvedKey = resp.data?.key;
      // El backend dice CÓMO se autentica lo que mandó. Por default el browser
      // recibe un JWT de 30 s ("bearer"), que no es una API key: no puede crear
      // credenciales ni leer el uso del proyecto, y caduca antes de que alguien
      // alcance a copiarlo. "token" es el modo viejo (key efímera de 10 min) y
      // queda solo para proyectos sin permiso de auth/grant.
      const authScheme = resp.data?.auth_scheme || "token";
      resolvedModel = resp.data?.model || resolvedModel;
      resolvedLanguage = resp.data?.language || resolvedLanguage;
      // NO loguear ni un prefijo de la credencial: la consola de un médico
      // queda abierta en el hospital y se comparte en capturas de pantalla.
      log("credencial recibida:", authScheme, "· model:", resolvedModel);
      if (!resolvedKey) throw new Error("Backend no devolvió key Deepgram.");

      // Cronómetro de audio real: arranca recién con key válida (si el token
      // falla no hubo dictado y no hay nada que cobrar).
      startedAtRef.current = Date.now();
      reportedRef.current = 0;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(reportUsage, 60000);

      // 3. Abrir WebSocket. Pasamos el sample rate REAL del AudioContext
      // (iOS suele dar 44100/48000, no honra el 16000 que pedimos).
      const actualSampleRate = audioCtx.sampleRate || TARGET_SAMPLE_RATE;
      const params = new URLSearchParams({
        model: resolvedModel,
        language: resolvedLanguage,
        interim_results: "true",
        smart_format: "true",
        punctuate: "true",
        encoding: "linear16",
        sample_rate: String(Math.round(actualSampleRate)),
        channels: "1",
        mip_opt_out: "true",
        // Corte de tramo por silencio. Sin declararlo, `speech_final` depende
        // del default del modelo y el borde queda impredecible. 700 ms es una
        // pausa natural entre frases dictadas, sin cortar a mitad de una idea.
        endpointing: "700",
      });
      // `keyterm` va repetido, uno por término, y TODO esto termina en la URL
      // del handshake. Deepgram la rechaza con HTTP 400 pasados los ~2.000
      // caracteres: con 200 términos el dictado directamente no arrancaba
      // (`WS close 1006`). Por eso el corte es por LARGO y no por cantidad —
      // los términos clínicos varían mucho de largo y contar términos no
      // protege de nada.
      const TOPE_URL = 1800;
      let cortados = 0;
      (keyterms || []).forEach((t) => {
        const termino = String(t || "").trim();
        if (!termino) return;
        if (params.toString().length + termino.length + 10 > TOPE_URL) {
          cortados += 1;
          return;
        }
        params.append("keyterm", termino);
      });
      if (cortados) log(`${cortados} keyterms quedaron fuera por largo de URL`);
      const wsUrl = `wss://api.deepgram.com/v1/listen?${params.toString()}`;
      log("abriendo WS:", `${resolvedModel} / ${resolvedLanguage} · ${(keyterms || []).length} keyterms`);
      // Ojo: el JWT SOLO entra con el subprotocolo "bearer". Mandarlo como
      // "token" devuelve 401 en el handshake (probado el 01-sep-2026).
      const ws = new WebSocket(wsUrl, [authScheme, resolvedKey]);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        log("WS open ✓");
        if (sesionRef.current) {
          sesionRef.current.tOpen = Date.now();
          logActivity("dictado_abierto", {
            module: "dictado",
            meta: {
              source: sesionRef.current.origen,
              kind: sesionRef.current.aparato,
              // Lo que el médico esperó entre apretar y poder hablar.
              duration_ms: Date.now() - sesionRef.current.t0,
            },
          });
        }
        startingRef.current = false;
        setStatus("recording");
        setRecording(true);
        // Deepgram cierra la conexión tras ~10s sin audio (NET-0001). En iOS
        // basta con que la pantalla se bloquee o la PWA pase a segundo plano
        // para que el AudioContext se suspenda y deje de mandar bytes: el
        // KeepAlive mantiene el canal vivo mientras el médico vuelve.
        if (keepAliveRef.current) clearInterval(keepAliveRef.current);
        keepAliveRef.current = setInterval(() => {
          const sock = wsRef.current;
          if (!sock || sock.readyState !== WebSocket.OPEN) return;
          try { sock.send(JSON.stringify({ type: "KeepAlive" })); } catch (_) {}
        }, 5000);
        // Vaciar lo que el médico dictó mientras conectábamos.
        if (pendientes.length) {
          log(`enviando ${pendientes.length} buffers dictados antes del open`);
          pendientes.forEach((buf) => {
            try { ws.send(buf); } catch (_) {}
          });
          pendientes.length = 0;
        }
      };

      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          if (data.type === "Results" || data.channel) {
            const transcript = data.channel?.alternatives?.[0]?.transcript || "";
            if (!transcript) return;
            if (data.is_final) {
              // Se cuenta el LARGO, jamás el contenido: es lo que permite
              // distinguir "dictó y salió" de "dictó y no salió nada" sin que
              // una sola palabra clínica salga del navegador.
              if (sesionRef.current) {
                sesionRef.current.chars += transcript.length;
                sesionRef.current.tramos += 1;
              }
              onFinal?.(transcript);
              // `speech_final` = Deepgram detectó el silencio, no solo el fin de
              // una frase. Ese es el borde del tramo.
              if (data.speech_final) onFinDeTramo?.(transcript);
            } else {
              onPartial?.(transcript);
            }
          }
        } catch (e) {
          // Ignorar mensajes no-JSON (ej. metadata).
        }
      };

      ws.onerror = (e) => {
        log("WS error", e);
        marcarMotivo("ws_error");
        setStatus("error");
        onError?.(e);
      };

      ws.onclose = (ev) => {
        log("WS close — code:", ev?.code, "reason:", ev?.reason);
        // Distinguir el cierre que pidió el usuario del que se cayó solo (red,
        // app en background, Deepgram cortando). El consumidor necesita saberlo
        // para NO borrar el tramo ya dictado y todavía no volcado: antes
        // cualquier cierre inesperado lo hacía desaparecer de la pantalla sin
        // dejarlo en ninguna celda, que es pérdida de trabajo clínico.
        const fueIntencional = closingRef.current;
        // El código de cierre es el dato que distingue "se cayó la red" (1006)
        // de "Deepgram rechazó el handshake" (1008/1011). Sin él, un dictado
        // roto y uno cancelado se ven exactamente igual en el log.
        if (!fueIntencional) {
          const code = Number(ev?.code);
          marcarMotivo(Number.isFinite(code) && code ? `ws_corte_${code}` : "ws_corte");
        }
        cleanup();
        if (!fueIntencional) onUnexpectedClose?.(ev);
      };
    } catch (err) {
      // #1887 (R62): `asr/realtime-token/` respondió 403 de jornadas (`sin_jornadas`, `requiere_boton`, `ventana_terminada`). No es una
      // falla del micrófono ni del backend: `JornadaAvisosGate` ya muestra el aviso (comprar o activar con la hora a la vista) y acá
      // el dictado vuelve a reposo SIN `onError`, que lo contaba como "falló el dictado" y pintaba un error técnico.
      if (esCorteDeJornada(err)) {
        log("dictado cortado por jornadas:", err?.response?.data?.code);
        marcarMotivo("sin_jornadas");
        cleanup();
        setStatus("idle");
        return;
      }
      log("EXCEPCIÓN en start():", err?.name, err?.message);
      // El nombre del error dice qué hacer: un permiso denegado se arregla en
      // los ajustes del navegador, un backend sin credencial se arregla acá.
      // Meterlos en la misma bolsa "falló el dictado" es lo que hace que un
      // problema de 30 segundos se investigue durante una semana.
      const nombre = String(err?.name || "");
      let motivo = "excepcion";
      if (nombre === "NotAllowedError" || nombre === "SecurityError") motivo = "permiso_denegado";
      else if (nombre === "NotFoundError" || nombre === "OverconstrainedError") motivo = "sin_microfono";
      else if (nombre === "NotReadableError") motivo = "microfono_ocupado";
      else if (String(err?.message || "").includes("key Deepgram")) motivo = "sin_credencial";
      marcarMotivo(motivo);
      cleanup();
      setStatus("error");
      onError?.(err);
    }
  }, [recording, onPartial, onFinal, onFinDeTramo, onError, onUnexpectedClose, keyterms, cleanup, reportUsage, abrirSesionTelemetria, marcarMotivo]);

  const stop = useCallback(() => {
    if (!wsRef.current) {
      cleanup();
      return;
    }
    closingRef.current = true;
    try {
      // Pedir a Deepgram que emita los finals pendientes antes de cerrar.
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "CloseStream" }));
      }
    } catch (_) {}
    // Cierre forzado tras 2s por si Deepgram no responde. El id se guarda para
    // poder cancelarlo desde start(): ver el comentario de `closeTimerRef`.
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      cleanup();
    }, 2000);
  }, [cleanup]);

  return { start, stop, recording, status };
}
